import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo2 from '../assets/Logo2.png';
import { queryCache } from '../lib/queryCache';
import { getStudyGroups } from '../services/api';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  // --- 1. GET USER DATA FROM CONTEXT ---
  // Replaces localStorage.getItem('user')
  const { user } = useAuth();
  const userData = user || { firstName: 'Student' };

  // --- Track Pending Join Requests & Unread Messages for Study Groups ---
  const [hasPendingRequests, setHasPendingRequests] = useState(() => {
    const cached = queryCache.get("study_groups_mapped");
    if (Array.isArray(cached)) {
      return cached.some((g) => !g.isShared && Number(g.pendingRequestsCount || 0) > 0);
    }
    return false;
  });

  const [hasUnreadMessages, setHasUnreadMessages] = useState(() => {
    const cached = queryCache.get("study_groups_mapped");
    if (Array.isArray(cached)) {
      return cached.some((g) => Number(g.unreadMessagesCount || 0) > 0);
    }
    return false;
  });

  useEffect(() => {
    const checkPending = () => {
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached)) {
        setHasPendingRequests(
          cached.some((g) => !g.isShared && Number(g.pendingRequestsCount || 0) > 0)
        );
      } else {
        getStudyGroups()
          .then((groups) => {
            if (Array.isArray(groups)) {
              setHasPendingRequests(
                groups.some(
                  (g) =>
                    !g.isShared &&
                    Number(g.pendingRequestsCount || (g.joinRequests ? g.joinRequests.length : 0)) > 0
                )
              );
            }
          })
          .catch(() => {});
      }
    };

    const checkUnread = () => {
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached)) {
        setHasUnreadMessages(
          cached.some((g) => Number(g.unreadMessagesCount || 0) > 0)
        );
      } else {
        getStudyGroups()
          .then((groups) => {
            if (Array.isArray(groups)) {
              setHasUnreadMessages(
                groups.some((g) => Number(g.unreadMessagesCount || 0) > 0)
              );
            }
          })
          .catch(() => {});
      }
    };

    if (!Array.isArray(queryCache.get("study_groups_mapped"))) {
      checkPending();
      checkUnread();
    }

    const handleRequestsChanged = (e) => {
      if (e?.detail && typeof e.detail.totalPendingRequests === "number") {
        setHasPendingRequests(e.detail.totalPendingRequests > 0);
        return;
      }

      if (e?.detail && typeof e.detail.pendingRequestsCount === "number") {
        const cached = queryCache.get("study_groups_mapped");
        if (Array.isArray(cached) && e.detail.groupId) {
          const updated = cached.map((g) =>
            String(g.id) === String(e.detail.groupId)
              ? { ...g, pendingRequestsCount: Number(e.detail.pendingRequestsCount) }
              : g
          );
          setHasPendingRequests(
            updated.some((g) => !g.isShared && Number(g.pendingRequestsCount || 0) > 0)
          );
          return;
        }
      }

      checkPending();
    };

    const handleMessageReceived = () => {
      setHasUnreadMessages(true);
    };

    const handleChatRead = (e) => {
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached) && e?.detail?.groupId) {
        const updated = cached.map((g) =>
          String(g.id) === String(e.detail.groupId)
            ? { ...g, unreadMessagesCount: 0, hasUnreadMessages: false }
            : g
        );
        setHasUnreadMessages(
          updated.some((g) => Number(g.unreadMessagesCount || 0) > 0)
        );
        return;
      }
      checkUnread();
    };

    window.addEventListener("study_group_requests_changed", handleRequestsChanged);
    window.addEventListener("study_group_updated", checkPending);
    window.addEventListener("study_group_message_received", handleMessageReceived);
    window.addEventListener("study_group_chat_read", handleChatRead);

    return () => {
      window.removeEventListener("study_group_requests_changed", handleRequestsChanged);
      window.removeEventListener("study_group_updated", checkPending);
      window.removeEventListener("study_group_message_received", handleMessageReceived);
      window.removeEventListener("study_group_chat_read", handleChatRead);
    };
  }, []);

  // --- 2. GET AVATAR URL ---
  const getAvatarUrl = () => {
    if (userData.avatar) return userData.avatar;
    const seed = userData.firstName || 'Student';
    return `https://api.dicebear.com/9.x/adventurer/svg?seed=${seed}&backgroundColor=b6e3f4`;
  };

  // --- NAV ITEM COMPONENT ---
  const NavItem = ({ to, icon, label, variant = 'default', badge = false }) => {
    const active = isActive(to);
    const isGroupVariant = variant === 'group';

    return (
      <Link
        to={to}
        onClick={toggleSidebar}
        className={`flex items-center justify-between px-4 py-3.5 rounded-xl font-medium transition-all duration-200 group mb-1 ${isGroupVariant
            ? active
              ? 'bg-blue-600 text-white shadow-md shadow-blue-200 translate-x-1'
              : 'text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:translate-x-1'
            : active
              ? 'bg-blue-600 text-white shadow-md shadow-blue-200 translate-x-1'
              : 'text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:translate-x-1'
          }`}
      >
        <div className="flex items-center min-w-0">
          <i className={`fa-solid ${icon} w-6 text-center text-lg mr-2 transition-colors ${active
              ? 'text-white'
              : isGroupVariant
                ? 'text-gray-400 group-hover:text-blue-600'
                : 'text-gray-400 group-hover:text-blue-600'
            }`}></i>
          <span className="truncate">{label}</span>
        </div>
        {badge && (
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse shrink-0"></span>
        )}
      </Link>
    );
  };

  // --- PROFILE SECTION COMPONENT ---
  const ProfileSection = () => {
    const isProfileActive = isActive('/profile');
    const avatarSrc = getAvatarUrl();

    return (
      <div className="p-4 border-t border-gray-100 bg-white">
        <Link
          to="/profile"
          onClick={toggleSidebar}
          className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 group border ${isProfileActive
              ? 'bg-blue-50 border-blue-100 shadow-sm' // ACTIVE STATE
              : 'bg-transparent border-transparent hover:bg-blue-50 hover:border-blue-100' // INACTIVE STATE
            }`}
        >
          {/* --- AVATAR IMAGE --- */}
          <div className="relative">
            <img
              src={avatarSrc}
              alt="Profile"
              className={`w-10 h-10 rounded-full object-cover border-2 transition-all duration-300 ${isProfileActive
                  ? 'border-blue-600'
                  : 'border-blue-100 group-hover:border-blue-400'
                }`}
            />
          </div>

          {/* Text Info */}
          <div className="flex-1 min-w-0 transition-all">
            <p className={`text-sm font-bold truncate transition-colors ${isProfileActive ? 'text-blue-900' : 'text-gray-900 group-hover:text-blue-900'
              }`}>
              {userData.firstName || userData.fullName || "Student"} {userData.lastName || ""}
            </p>
            <p className={`text-xs truncate transition-colors ${isProfileActive ? 'text-blue-600 font-medium' : 'text-gray-500 group-hover:text-blue-600'
              }`}>
              View Profile
            </p>
          </div>

          {/* Arrow */}
          <i className={`fa-solid fa-chevron-right text-xs transition-all duration-300 ${isProfileActive
              ? 'text-blue-600 translate-x-1'
              : 'text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1'
            }`}></i>
        </Link>
      </div>
    );
  };

  return (
    <>
      {/* --- DESKTOP SIDEBAR --- */}
      <aside className="w-64 bg-white border-r border-gray-200 flex-col hidden md:flex h-screen sticky top-0 z-30 font-sans">

        {/* --- HEADER --- */}
        <div className="h-20 flex items-center px-6 border-b border-gray-50 bg-gradient-to-b from-white to-gray-50/50">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3 shadow-lg shadow-blue-200 ring-2 ring-blue-50 bg-white overflow-hidden">
            <img src={Logo2} alt="ReviewMate logo" className="w-full h-full object-contain rounded-xl scale-150" />
          </div>
          <div>
            <span className="text-lg font-bold text-gray-900 tracking-tight leading-none block">ReviewMate</span>
          </div>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto custom-scrollbar">
          <NavItem to="/dashboard" icon="fa-house" label="Dashboard" />
          {/*<NavItem to="/board-prep" icon="fa-graduation-cap" label="Exam Prep" />*/}
          <NavItem to="/library" icon="fa-book-open" label="My Library" />
          <NavItem to="/study-group" icon="fa-users" label="Study Group" variant="group" badge={hasPendingRequests || hasUnreadMessages} />
          <NavItem to="/analytics" icon="fa-chart-pie" label="Analytics" />
          <NavItem to="/achievements" icon="fa-trophy" label="Achievement" />
        </nav>

        <ProfileSection />
      </aside>

      {/* --- MOBILE SIDEBAR --- */}
      <div
        className={`fixed inset-y-0 left-0 w-72 bg-white border-r border-gray-200 z-50 flex flex-col md:hidden transition-transform duration-300 ease-in-out shadow-2xl ${isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="h-20 flex items-center justify-between px-6 border-b border-gray-50 bg-gray-50/50">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 shadow-md bg-white overflow-hidden">
              <img src={Logo2} alt="ReviewMate logo" className="w-full h-full object-contain rounded-lg scale-150" />
            </div>
            <span className="text-xl font-bold text-gray-900 tracking-tight">ReviewMate</span>
          </div>
          <button
            onClick={toggleSidebar}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all shadow-sm"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          <NavItem to="/dashboard" icon="fa-house" label="Dashboard" />
          {/*<NavItem to="/board-prep" icon="fa-graduation-cap" label="Board Prep" />*/}
          <NavItem to="/library" icon="fa-book-open" label="My Library" />
          <NavItem to="/study-group" icon="fa-users" label="Study Group" variant="group" badge={hasPendingRequests || hasUnreadMessages} />
          <NavItem to="/analytics" icon="fa-chart-pie" label="Analytics" />
          <NavItem to="/achievements" icon="fa-trophy" label="Achievement" />
        </nav>

        <ProfileSection />
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
          onClick={toggleSidebar}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
